'use client';
import React, { useEffect, useState } from 'react';
import Styles from './payment-button.module.scss';
import {
  useErrorMessage,
  useHandlePaymentClicked,
  useIsCreditCardValid,
  useResetSubmitPaymentClicked,
  useSetBillingAddress,
  useSetCreditCardValid,
  useSubmitPaymentClicked,
  useUserSelectedAmount,
  useBillingAddressState,
  useSelectedPaymentOption,
  usePaymentReview,
  useSelectedAmountOption,
} from '~/store/wmap-store';
import { isBillingFormInValid } from '~/components/billing-address/components/utils';
import SpinningLoader from '../spinning-loader';
import useUtagLoaded from '~/hooks/use-utag-loaded';
import { sendTealiumPageView } from '~/utils/tealium';
import { formatCurrency } from '~/utils/utils';
import { usePathname, useRouter } from 'next/navigation';
import { TYPES, WMAP_PREFIX } from '~/modules/payment/constants';
import AffirmCheckout from '~/modules/payment/affirm';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import { BuyNowPayLaterAffirmLeasing } from '~/gql/__generated__/types';
import { useOrderInprogress } from '~/hooks/use-order-inprogress';
import { INPUT_ID_OTHER_AMOUNT } from '~/constant/constants';

interface IPaymentButtonProps {
  affirmLeasingConfiguration?: BuyNowPayLaterAffirmLeasing;
  orderDetails?: ISalesOrderResponse;
}

const PaymentButton: React.FC<IPaymentButtonProps> = ({
  affirmLeasingConfiguration,
  orderDetails,
}) => {
  const [
    errorMessage,
    userSelectedAmount,
    submitPaymentClicked,
    billingAddressState,
    handleSubmitPayment,
    resetSubmitPaymentClicked,
    setBillingAddress,
    setCreditCardValid,
    isCreditCardValid,
    selectedPayment,
    activeOption,
  ] = [
    useErrorMessage(),
    useUserSelectedAmount(),
    useSubmitPaymentClicked(),
    useBillingAddressState(),
    useHandlePaymentClicked(),
    useResetSubmitPaymentClicked(),
    useSetBillingAddress(),
    useSetCreditCardValid(),
    useIsCreditCardValid(),
    useSelectedPaymentOption(),
    useSelectedAmountOption(),
  ];

  const { acceptContent: storeAcceptContent, cartId: storeCartId } =
    useOrderInprogress();

  const paymentAmount = formatCurrency(Number(userSelectedAmount) || 0);
  const hasBillingAddressErrors = isBillingFormInValid(billingAddressState);
  const loaderColor = 'inherit';
  const [isLoading, setIsLoading] = useState(false);
  const utagLoaded = useUtagLoaded();
  const [hasRun, setHasRun] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    if (utagLoaded && !hasRun && window.utag) {
      sendTealiumPageView(window.utag.data);
      setHasRun(true);
    }
  }, [utagLoaded, hasRun]);

  useEffect(() => {
    if (pathname) {
      setIsLoading(false);
      resetSubmitPaymentClicked();
      setBillingAddress({});
      setCreditCardValid(false);
    }
  }, [
    pathname,
    resetSubmitPaymentClicked,
    router,
    setBillingAddress,
    setCreditCardValid,
  ]);

  const handleClick = async () => {
    setIsLoading(true);

    try {
      handleSubmitPayment();
    } catch (error) {
      console.error('Payment submission failed:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (errorMessage) {
      setIsLoading(false);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (!submitPaymentClicked) {
      setIsLoading(false);
    }
  }, [submitPaymentClicked]);
  //do not render this component for payment review page
  const paymentReview = usePaymentReview();
  if (paymentReview.review) {
    return <></>;
  }

  return (
    <div
      className={`${Styles.paymentButtonWrapper} ${selectedPayment === TYPES.AFFIRM ? Styles.affirmButtonWrapper : ''}`}
    >
      {selectedPayment == TYPES.AFFIRM ? (
        storeAcceptContent &&
        affirmLeasingConfiguration &&
        orderDetails &&
        storeCartId && (
          <AffirmCheckout
            acceptContent={storeAcceptContent}
            affirmLeasingConfiguration={affirmLeasingConfiguration}
            checkoutCart={orderDetails}
            cartId={storeCartId}
            isPaymentBtn={true}
            isDisabled={
              activeOption === INPUT_ID_OTHER_AMOUNT || hasBillingAddressErrors
            }
          />
        )
      ) : (
        <button
          className={`${Styles.paymentButton} ${isLoading ? Styles.loading : ''}`}
          tabIndex={0}
          disabled={
            !!errorMessage ||
            !isCreditCardValid ||
            hasBillingAddressErrors ||
            isLoading ||
            selectedPayment === TYPES.SYNCHRONY ||
            selectedPayment === TYPES.PROGRESIVE ||
            selectedPayment === TYPES.AFFIRM
          }
          onClick={handleClick}
          id={`${WMAP_PREFIX}-pay-now-btn`}
        >
          {isLoading ? (
            <SpinningLoader height="22px" width="22px" color={loaderColor} />
          ) : (
            <>
              <span className={Styles.paymentAmount}>{paymentAmount} -</span>
              Submit Payment
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default PaymentButton;
